/* ---------------------------------------------------------------------------
   OUMO promo runtime
   ---------------------------------------------------------------------------
   Drives the four pieces of the campaign UI:

     product page   the tabbed offer card  ([data-oumo-offer])
                    the nudge above Add to cart ([data-oumo-nudge])
                    the poster is static and needs nothing here
     cart drawer    one track with four stops, injected into the drawer header
                    the "See all" dialog listing every offer

   Counting comes from /cart?view=promo, never /cart.js: only Liquid can see
   product tags, and without them the Seel "Worry-Free Purchase" line and
   already-discounted sets get counted as tier progress.

   The drawer is re-rendered wholesale by the theme on every cart change and
   takes the injected rail with it, so we watch it and re-inject. The observer
   is guarded while we mutate, or our own injection would retrigger it.
--------------------------------------------------------------------------- */

(function () {
  'use strict';

  var CFG = window.OUMO_PROMO || {};
  var TIERS = parseTiers(CFG.tiers || '2:20');
  var TITLE = CFG.title || 'Spooky savings';
  var SET_PCT = parseInt(CFG.setPct, 10) || 20;
  var OFFER_NOTE = CFG.offerNote || 'Mix & match · excludes sale items & sets';
  var SET_NOTE = CFG.setNote || 'Applies to every piercing set';
  var SHIP_NOTE = CFG.shipNote || 'Orders over $79';
  var FOOT_NOTE = CFG.footNote || 'Sale items excluded. Sets qualify separately and do not count toward the 2-item offer. Offers do not stack on the same item.';

  var cart = null;      /* last /cart?view=promo payload */
  var injecting = false;
  var pending = null;

  var ICON_TRUCK = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 7h10v9H3zM13 10h4l3 3v3h-7z"/><circle cx="7" cy="18" r="1.6"/><circle cx="17" cy="18" r="1.6"/></svg>';
  var ICON_CHEVRON = '<svg viewBox="0 0 12 12" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M2.5 4.5 6 8l3.5-3.5"/></svg>';

  function parseTiers(raw) {
    return String(raw)
      .split(',')
      .map(function (pair) {
        var b = pair.split(':');
        return { qty: parseInt(b[0], 10), pct: parseInt(b[1], 10) };
      })
      .filter(function (t) { return t.qty > 0 && t.pct > 0; })
      .sort(function (a, b) { return a.qty - b.qty; });
  }

  function state(count) {
    var reached = null, next = null;
    for (var i = 0; i < TIERS.length; i++) {
      if (count >= TIERS[i].qty) { reached = TIERS[i]; }
      else if (!next) { next = TIERS[i]; }
    }
    return { reached: reached, next: next, count: count };
  }

  function plural(n, one, many) { return n === 1 ? one : many; }
  function routeRoot() { return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/'; }
  function el(tag, cls) { var n = document.createElement(tag); if (cls) { n.className = cls; } return n; }

  /* === product page: tabs ================================================ */

  function initTabs() {
    document.querySelectorAll('[data-oumo-offer]').forEach(function (card) {
      if (card.dataset.oumoTabsReady) return;
      var tabs = card.querySelectorAll('[data-oumo-tab]');
      var panels = card.querySelectorAll('[data-oumo-panel]');
      if (!tabs.length) return;
      card.dataset.oumoTabsReady = '1';
      function show(index) {
        tabs.forEach(function (tab, i) {
          tab.setAttribute('aria-selected', i === index ? 'true' : 'false');
          tab.tabIndex = i === index ? 0 : -1;
        });
        panels.forEach(function (panel, i) { panel.hidden = i !== index; });
      }
      card.addEventListener('click', function (e) {
        var tab = e.target.closest ? e.target.closest('[data-oumo-tab]') : null;
        if (!tab || !card.contains(tab)) return;
        show(Array.prototype.indexOf.call(tabs, tab));
      });
      card.addEventListener('keydown', function (e) {
        var tab = e.target.closest ? e.target.closest('[data-oumo-tab]') : null;
        if (!tab || !card.contains(tab)) return;
        var index = Array.prototype.indexOf.call(tabs, tab), next;
        if (e.key === 'ArrowRight') next = (index + 1) % tabs.length;
        else if (e.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
        else if (e.key === 'Home') next = 0;
        else if (e.key === 'End') next = tabs.length - 1;
        else return;
        e.preventDefault();
        show(next);
        tabs[next].focus();
      });
    });
  }

  /* === product page: nudge + tier highlight =============================== */

  function paintNudge(count) {
    var node = document.querySelector('[data-oumo-nudge]');
    if (!node) { return; }
    if (node.hasAttribute('data-set-pct')) { return; }   /* sets are static */

    var out = node.querySelector('[data-oumo-nudge-text]');
    var s = state(count);

    node.hidden = false;
    node.classList.toggle('is-on', !!s.reached);
    var message = s.reached ? (s.next ? '<b>' + s.reached.pct + '% off</b> unlocked' : 'You’ve unlocked <b>' + s.reached.pct + '% off</b>') : '';
    if (s.next) {
      var gap = s.next.qty - s.count;
      message += s.reached ? ' · Add ' + gap + ' more ' + plural(gap, 'item', 'items') + ' for ' + s.next.pct + '% off' :
        'Add ' + gap + (count > 0 ? ' more ' : ' ') + plural(gap, 'item', 'items') + ' for <b>' + s.next.pct + '% off</b>';
    }
    if (out) { out.innerHTML = message || 'Discount applied automatically at checkout.'; }
  }

  function paintTiers(count) {
    var wrap = document.querySelector('[data-oumo-tiers]');
    if (!wrap) { return; }
    var s = state(count);
    var cells = wrap.querySelectorAll('[data-oumo-tier]');
    for (var i = 0; i < cells.length; i++) {
      var q = parseInt(cells[i].getAttribute('data-oumo-tier'), 10);
      var active = !!(s.reached && q === s.reached.qty);
      cells[i].classList.toggle('is-on', active);
      if (active) { cells[i].setAttribute('aria-current', 'true'); }
      else { cells[i].removeAttribute('aria-current'); }
      var label = cells[i].querySelector('[data-oumo-tier-state]');
      if (label) { label.textContent = active ? '✓ Unlocked' : q + '+ pieces'; }
    }
  }

  function paintSetPrice(variantId) {
    var tag = document.querySelector('[data-oumo-setmap]');
    if (!tag || !variantId) { return; }
    var map;
    try { map = JSON.parse(tag.textContent); } catch (e) { return; }
    var row = map[String(variantId)];
    if (!row) { return; }
    var was = document.querySelector('[data-oumo-was]');
    var now = document.querySelector('[data-oumo-now]');
    var save = document.querySelector('[data-oumo-save]');
    if (was) { was.textContent = row.was; }
    if (now) { now.textContent = row.now; }
    if (save) { save.textContent = 'You save ' + row.save; }
  }

  function currentVariantId() {
    var i = document.querySelector('product-info form[action*="/cart/add"] input[name="id"]:not([disabled])');
    if (i && i.value) { return i.value; }
    try {
      var u = new URLSearchParams(location.search).get('variant');
      if (u) { return u; }
    } catch (e) {}
    i = document.querySelector('form[action*="/cart/add"] input[name="id"]:not([disabled])');
    return i ? i.value : null;
  }

  /* Cart milestones use independent eligibility and shipping states. */
  var ICON_SPARKLE = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" aria-hidden="true"><path d="m12 3 2.4 6.6L21 12l-6.6 2.4L12 21l-2.4-6.6L3 12l6.6-2.4L12 3ZM20 2v4m-2-2h4"/></svg>';
  var sheetReturn = null, sheetInert = [], sheetClosing = null, sheetFocusCleanup = null;
  function buildRail() {
    var rail = el('div', 'oumo-cart-progress');
    rail.setAttribute('data-oumo-rail', '');
    var milestones = el('div', 'oumo-cart-progress__milestones');
    var line = el('div', 'oumo-cart-progress__line');
    line.setAttribute('aria-hidden', 'true');
    TIERS.forEach(function () { line.appendChild(el('span', '')); });
    milestones.appendChild(line);
    var stops = [{title: '0 pieces', benefit: 'Start here', icon: '0'}];
    TIERS.forEach(function(t) { stops.push({title:t.qty+'+ pieces', benefit:t.pct+'% off', icon:String(t.qty)}); });
    stops.forEach(function(s, i) {
      var stop = el('div','oumo-cart-progress__stop');
      stop.setAttribute('data-oumo-stop',String(i));
      var node=el('span','oumo-cart-progress__node'); node.innerHTML=s.icon;
      var title=el('span','oumo-cart-progress__qty'); title.textContent=s.title;
      var benefit=el('strong','');benefit.textContent=s.benefit;
      var earned=el('span','oumo-cart-progress__earned');
      benefit.appendChild(earned);stop.append(node,title,benefit);milestones.appendChild(stop);
    });
    rail.appendChild(milestones);
    var shipping=el('div','oumo-cart-progress__shipping');
    shipping.innerHTML=ICON_TRUCK+'<span>Free shipping · Over US$79</span><span data-oumo-shipping-state></span>';
    rail.appendChild(shipping);
    var offers=el('button','oumo-cart-progress__offers');offers.type='button';
    offers.setAttribute('data-oumo-open-offers','');offers.setAttribute('aria-haspopup','dialog');offers.setAttribute('aria-expanded','false');offers.setAttribute('aria-controls','OumoCartOffers');
    offers.innerHTML='<span class="oumo-cart-progress__title">'+ICON_SPARKLE+'<span>Spooky Savings</span></span>'+ICON_CHEVRON;
    rail.appendChild(offers);return rail;
  }
  function paintRail(rail,data) {
    var earned=[true].concat(TIERS.map(function(t){return data.eligible>=t.qty;}));
    rail.querySelectorAll('[data-oumo-stop]').forEach(function(stop,i){
      stop.classList.toggle('is-on',earned[i]);
      var label=stop.querySelector('.oumo-cart-progress__earned');
      var text=i>0&&earned[i]?'✓':'';if(label.textContent!==text)label.textContent=text;
    });
    rail.querySelectorAll('.oumo-cart-progress__line span').forEach(function(segment,i){
      var from=i===0?0:TIERS[i-1].qty,to=TIERS[i].qty;
      var progress=Math.max(0,Math.min(1,(data.eligible-from)/(to-from)));
      segment.style.setProperty('--oumo-progress', (progress*100)+'%');
    });
    rail.querySelector('[data-oumo-shipping-state]').textContent=data.ship_ok?'✓':'';
    rail.querySelector('.oumo-cart-progress__shipping').classList.toggle('is-on',!!data.ship_ok);
  }
  function syncRail(data) {
    var drawer=document.querySelector('#CartDrawer');if(!drawer)return;
    // The new drawer campaign owns its offer; do not inject the previous campaign.
    if(drawer.hasAttribute('data-oumo-cart-campaign')){
      var previousRail=drawer.querySelector('[data-oumo-rail]');
      if(previousRail)previousRail.remove();
      closeDialog();
      return;
    }
    var header=drawer.querySelector('.cart-drawer__header');if(!header)return;
    var rail=header.querySelector('[data-oumo-rail]'),goal=header.querySelector('free-shipping-goal');
    if(!data||!data.total){if(rail)rail.remove();if(goal)goal.style.removeProperty('display');closeDialog();return;}
    if(goal)goal.style.setProperty('display','none','important');
    if(!rail){rail=buildRail();header.appendChild(rail);}
    paintRail(rail,data);
  }
  function offerRows(data) {
    var rows=TIERS.map(function(t){return {title:'Buy any '+t.qty+' items',tag:t.pct+'% off',done:(data.eligible||0)>=t.qty};});
    rows.push({title:'Shop any set',tag:SET_PCT+'% off',done:!!data.set_applied});
    rows.push({title:'Shop final sale items',tag:'Up to 60% off',sale:true});
    rows.push({title:'Orders over US$79',tag:'Free shipping',done:!!data.ship_ok});return rows;
  }
  function ensureDialog() {
    var inner=document.querySelector('#CartDrawer .drawer__inner');if(!inner)return null;
    var dlg=inner.querySelector('[data-oumo-offers]');if(dlg)return dlg;
    dlg=el('div','oumo-cart-sheet');dlg.id='OumoCartOffers';dlg.setAttribute('data-oumo-offers','');dlg.hidden=true;
    var backdrop=el('div','oumo-cart-sheet__backdrop');backdrop.setAttribute('data-oumo-close-offers','');backdrop.setAttribute('aria-hidden','true');
    var card=el('section','oumo-cart-sheet__card');card.setAttribute('role','dialog');card.setAttribute('aria-modal','true');card.setAttribute('aria-labelledby','OumoCartOffersTitle');
    var handle=el('div','oumo-cart-sheet__handle');handle.setAttribute('aria-hidden','true');
    var head=el('div','oumo-cart-sheet__head');var title=el('h3','');title.id='OumoCartOffersTitle';title.innerHTML=ICON_SPARKLE+'<span>Spooky Savings</span>';
    var close=el('button','oumo-cart-sheet__close');close.type='button';close.setAttribute('aria-label','Close offers');close.setAttribute('data-oumo-close-offers','');close.textContent='×';head.append(title,close);
    var list=el('div','');list.setAttribute('data-oumo-offers-list','');
    var foot=el('p','oumo-cart-sheet__foot');foot.textContent=FOOT_NOTE;
    var subtitle=el('p','oumo-cart-sheet__subtitle');subtitle.textContent='Big savings. Spooky season.';
    card.append(handle,head,subtitle,list,foot);dlg.append(backdrop,card);inner.appendChild(dlg);return dlg;
  }
  function paintDialog(data) {
    var dlg=document.querySelector('[data-oumo-offers]');if(!dlg)return;
    var list=dlg.querySelector('[data-oumo-offers-list]');list.replaceChildren();
    offerRows(data).forEach(function(row){
      var item=el(row.href?'a':'div','oumo-cart-sheet__row'+(row.href?' oumo-cart-sheet__offer-link':'')+(row.sale?' oumo-cart-sheet__sale-link':'')),title=el('span','oumo-cart-sheet__label');title.textContent=row.title;
      if(row.href)item.href=row.href;
      var tag=el('span','oumo-cart-sheet__tag'+(row.done?' is-on':''));var value=el('span','');value.textContent=row.tag;tag.appendChild(value);
      if(row.done){var applied=el('span','oumo-cart-sheet__applied');applied.textContent='✓';applied.setAttribute('aria-label','Threshold reached');tag.appendChild(applied);}
      item.append(title,tag);list.appendChild(item);
    });
  }
  function openDialog() {
    if(!cart)return;
    var dlg=ensureDialog();if(!dlg||dlg.classList.contains('is-open'))return;
    clearTimeout(sheetClosing);
    if(sheetFocusCleanup)sheetFocusCleanup();
    paintDialog(cart);sheetReturn=document.activeElement;
    var card=dlg.querySelector('[role="dialog"]'),close=dlg.querySelector('button');
    dlg.hidden=false;dlg.inert=false;
    // Establish the off-screen card position before starting its single transition.
    card.getBoundingClientRect();
    sheetInert=Array.from(dlg.parentElement.children).filter(function(n){return n!==dlg;}).map(function(n){var prev=n.inert;n.inert=true;return [n,prev];});
    var trigger=document.querySelector('[data-oumo-open-offers]');if(trigger)trigger.setAttribute('aria-expanded','true');
    var focusTimer;
    function cleanup(){clearTimeout(focusTimer);card.removeEventListener('transitionend',onEnd);sheetFocusCleanup=null;}
    function focusOpened(){
      cleanup();
      if(!dlg.isConnected||dlg.hidden||!dlg.classList.contains('is-open'))return;
      if(window.FoxTheme&&FoxTheme.a11y)FoxTheme.a11y.trapFocus(card,close);
      else close.focus({preventScroll:true});
    }
    function onEnd(event){if(event.target===card&&event.propertyName==='transform')focusOpened();}
    sheetFocusCleanup=cleanup;
    card.addEventListener('transitionend',onEnd);
    dlg.classList.add('is-open');
    // Reduced-motion users have no transitionend; the timer also covers interrupted transitions.
    if(window.matchMedia('(prefers-reduced-motion: reduce)').matches)focusOpened();
    else focusTimer=setTimeout(focusOpened,320);
  }
  function closeDialog() {
    var dlg=document.querySelector('[data-oumo-offers]');if(!dlg||dlg.hidden||!dlg.classList.contains('is-open'))return;
    if(sheetFocusCleanup)sheetFocusCleanup();
    dlg.classList.remove('is-open');dlg.inert=true;sheetInert.forEach(function(pair){pair[0].inert=pair[1];});sheetInert=[];
    var drawer=document.querySelector('#CartDrawer'),trigger=document.querySelector('[data-oumo-open-offers]');if(trigger)trigger.setAttribute('aria-expanded','false');
    if(drawer&&!drawer.hidden&&window.FoxTheme&&FoxTheme.a11y)FoxTheme.a11y.trapFocus(drawer,sheetReturn&&sheetReturn.isConnected?sheetReturn:trigger);
    sheetClosing=setTimeout(function(){dlg.hidden=true;},280);
  }

  /* === wiring ============================================================= */

  function repaint(data) {
    cart = data;
    // Drawer headers survive item-section replacements: refresh their snapshot.
    document.querySelectorAll('oumo-cart-dual-progress, oumo-shipping-offer').forEach(function(node) {
      if (!Number.isFinite(data.subtotal) || data.subtotal < 0) return;
      node.dataset.total = String(data.subtotal);
      node.dataset.currency = data.currency || 'USD';
      if (typeof node.connectedCallback === 'function') node.connectedCallback();
      var fill = node.querySelector('[data-eligible-fill]');
      var message = node.querySelector('[data-eligible-message]');
      var count = Math.max(0, data.eligible || 0);
      if (fill) fill.style.width = Math.min(100, count * 50) + '%';
      if (message) message.textContent = count >= 2 ? '✓ Unlocked' : count === 1 ? 'Add 1 more eligible item' : 'Add 2 eligible items';
    });
    paintNudge(data.eligible || 0);
    paintTiers(data.eligible || 0);
    syncRail(data);
    if (document.querySelector('[data-oumo-offers]:not([hidden])')) { paintDialog(data); }
  }

  function refresh() {
    fetch(routeRoot() + 'cart?view=promo', { cache: 'no-store' })
      .then(function (r) { if (!r.ok) { throw new Error('Promo cart unavailable'); } return r.text(); })
      .then(function (t) {
        var data = JSON.parse(t);
        if (!Number.isFinite(data.eligible) || data.eligible < 0) { throw new Error('Invalid eligible count'); }
        return data;
      })
      .then(repaint)
      .catch(function () {
        /* Never use total cart units as eligibility: sets, Sale and Seel
           would incorrectly unlock a tier. Keep static rules until recovery. */
        cart = null;
        syncRail(null);
        var node = document.querySelector('[data-oumo-nudge]:not([data-set-pct]) [data-oumo-nudge-text]');
        if (node) { node.textContent = 'Mix & match eligible pieces — discount applied at checkout.'; }
        document.querySelectorAll('[data-oumo-tier]').forEach(function (cell) {
          cell.classList.remove('is-on');
          cell.removeAttribute('aria-current');
          var label = cell.querySelector('[data-oumo-tier-state]');
          if (label) { label.textContent = cell.getAttribute('data-oumo-tier') + '+ pieces'; }
        });
      });
  }

  function schedule() {
    clearTimeout(pending);
    pending = setTimeout(refresh, 150);
  }

  function start() {
    initTabs();
    refresh();
    paintSetPrice(currentVariantId());

    document.addEventListener('click', function (e) {
      if (!e.target.closest) { return; }
      if (e.target.closest('[data-oumo-open-offers]')) { e.preventDefault(); openDialog(); return; }
      if (e.target.closest('[data-oumo-close-offers]')) { closeDialog(); }
    });

    document.addEventListener('keyup', function (e) {
      var sheet=document.querySelector('[data-oumo-offers].is-open');
      if (e.key === 'Escape' && sheet) { e.preventDefault(); e.stopImmediatePropagation(); closeDialog(); }
    }, true);

    document.addEventListener('change', function () {
      if (cart) { paintNudge(cart.eligible || 0); paintTiers(cart.eligible || 0); }
      paintSetPrice(currentVariantId());
    });

    document.addEventListener('input', function (e) {
      if (e.target && e.target.closest && e.target.closest('quantity-input') && cart) {
        paintNudge(cart.eligible || 0);
        paintTiers(cart.eligible || 0);
      }
    });

    document.addEventListener('shopify:section:load', function () { initTabs(); refresh(); paintSetPrice(currentVariantId()); });
    window.addEventListener('pageshow', function (event) { if (event.persisted) { schedule(); } });

    try {
      if (window.FoxTheme && FoxTheme.pubsub && FoxTheme.pubsub.PUB_SUB_EVENTS) {
        var E = FoxTheme.pubsub.PUB_SUB_EVENTS;
        if (E.cartUpdate) { FoxTheme.pubsub.subscribe(E.cartUpdate, schedule); }
        if (E.variantChange) {
          FoxTheme.pubsub.subscribe(E.variantChange, function (ev) {
            paintSetPrice(ev && ev.data && ev.data.variant ? ev.data.variant.id : currentVariantId());
          });
        }
      }
    } catch (e) {}

    var drawer = document.querySelector('#CartDrawer');
    if (drawer) {
      new MutationObserver(function (records) {
        if (!records.some(function(r){var n=r.target.nodeType===1?r.target:r.target.parentElement;return n&&!n.closest('[data-oumo-rail],[data-oumo-offers],oumo-cart-dual-progress,oumo-shipping-offer');})) return;
        schedule();
      }).observe(drawer, { childList: true, subtree: true });
      new MutationObserver(function(){if(drawer.hidden)closeDialog();else schedule();}).observe(drawer,{attributes:true,attributeFilter:['hidden']});
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();



if (!customElements.get('oumo-shipping-offer')) {
  customElements.define('oumo-shipping-offer', class extends HTMLElement {
    connectedCallback() {
      const currency = this.dataset.currency || 'USD';
      const rate = currency === 'USD' ? 1 : Number(window.Shopify?.currency?.rate);
      const total = Number(this.dataset.total);
      const badge = this.querySelector('strong');
      const check = this.querySelector('.oumo-offer-check');
      if (!badge || !check || !Number.isFinite(rate) || rate <= 0 || !Number.isFinite(total)) return;
      const qualified = total >= Math.round(7900 * rate);
      badge.classList.toggle('is-applied', qualified);
      check.hidden = !qualified;
      badge.title = qualified ? 'Free shipping threshold reached' : 'Orders over US$79';
    }
  });
}

/* Keep pointer-opened Today's Offers free of inherited keyboard focus rings. */
(() => {
  document.addEventListener('pointerdown', (event) => {
    if (!(event.target instanceof Element)) return;
    const target = event.target.closest('.oumo-cart-offers-trigger, .oumo-cart-offers');
    const drawer = target?.closest('#CartDrawer[data-oumo-cart-campaign]');
    if (drawer) drawer.setAttribute('data-oumo-offers-pointer', '');
  }, true);
  document.addEventListener('keydown', (event) => {
    if (!['Tab', 'Enter', ' ', 'Escape', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key)) return;
    document.querySelector('#CartDrawer[data-oumo-offers-pointer]')?.removeAttribute('data-oumo-offers-pointer');
  }, true);
})();

/* Read-only shipping progress; follows the active presentment currency. */
if (!customElements.get('oumo-cart-dual-progress')) {
  customElements.define('oumo-cart-dual-progress', class extends HTMLElement {
    connectedCallback() {
      const currency = this.dataset.currency || 'USD';
      const rate = currency === 'USD' ? 1 : Number(window.Shopify?.currency?.rate);
      const total = Number(this.dataset.total);
      if (!Number.isFinite(rate) || rate <= 0 || !Number.isFinite(total)) return;
      const target = Math.round(7900 * rate);
      const remaining = Math.max(0, target - total);
      this.querySelector('[data-shipping-fill]').style.width = Math.min(100, total / target * 100) + '%';
      this.querySelector('[data-shipping-message]').textContent = remaining === 0
        ? '✓ Unlocked'
        : new Intl.NumberFormat(document.documentElement.lang || 'en', {style:'currency',currency}).format(remaining / 100) + ' away from free shipping';
    }
  });
}
